// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * ClinicTeamList — the Master Account's "Clinic Team" page.
 *
 * Lists the master's Sub Accounts (users whose `masterAccount` is the current
 * vet) and lets the master add new ones via AddSubAccountModal → POST
 * /api/users/create-sub-account. The list populates once the backend grants a
 * master read access to its subs (the ada-core login-scoping step); until then
 * it shows the empty state, while creating still works.
 *
 * Mirrors the MUI-Card layout of src/vet-shares/VetSharesList.tsx.
 *
 * MASTER-ONLY. The sidebar entry is gated to masters (src/layout/Menu.tsx), but
 * the route stays registered for everyone, so a sub-account can still reach this
 * URL directly. Without the guard below it saw an empty team plus an "Add
 * Sub-Account" button whose POST 403s server-side ("Only a master clinic account
 * can create sub-accounts") — a dead end that reads like a broken page. Show the
 * explanation instead. The server is the real gate; this is only honest UI.
 */
import { Add as AddIcon, Groups as GroupsIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useGetIdentity, useGetList, useTranslate } from 'react-admin'
import EmptyState from '../components/EmptyState'
import PageHeader from '../components/PageHeader'
import ToneChip from '../components/ToneChip'
import type { StatusTone } from '../themes/statusColors'
import AddSubAccountModal from './AddSubAccountModal'
import SubAccountRowActions from './SubAccountRowActions'
import { effectiveSubAccountStatus } from './subAccountStatus'

type SubStatus = 'active' | 'pending_invitation' | 'disabled' | 'removed' | null | undefined

// VP-TEAM-R03. Tone from the SAME effective status the label shows (R04) —
// the color used to read the raw field, so a null-status sub was labeled
// "Pending invitation" but colored like a disabled account. Sub-account
// states are workflow, not clinical priority (UI-STYLE-GUIDE §3): only
// `active` takes the quiet positive tone; pending/disabled/removed are neutral.
const statusTone = (status: SubStatus | string): StatusTone =>
  status === 'active' ? 'positive' : 'neutral'

const ClinicTeamList: React.FC = () => {
  const translate = useTranslate()
  const { identity, isLoading: identityLoading } = useGetIdentity()
  const [modalOpen, setModalOpen] = useState(false)

  // A sub-account has a parent; a master does not. `masterAccount` is undefined
  // while identity loads, so hold on a spinner until it resolves instead of
  // flashing the master view (with its Add button) at a sub. If identity never
  // resolves the master view renders — the server 403 is still the real gate.
  const isSubAccount = !!identity?.masterAccount

  // Sub-accounts = users whose masterAccount is the current (master) vet.
  // `refetch` (not a meta key) is what actually re-pulls the list after a
  // create / disable / enable — meta changes don't reliably bust the cache.
  const { data, isLoading, error, refetch } = useGetList(
    'users',
    {
      filter: { masterAccount: identity?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: !!identity?.id && !isSubAccount },
  )

  const handleModalClose = (created: boolean) => {
    setModalOpen(false)
    if (created) refetch()
  }

  const subs = data ?? []

  if (identityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isSubAccount) {
    return (
      <Box>
        <PageHeader
          title={translate('pos.clinic_team.title')}
          subtitle={translate('pos.clinic_team.sub_account_notice')}
        />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={translate('pos.clinic_team.title')}
        subtitle={translate('pos.clinic_team.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            {translate('pos.clinic_team.add_button')}
          </Button>
        }
      />
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : subs.length === 0 ? (
            <EmptyState icon={<GroupsIcon />} title={translate('pos.clinic_team.empty')} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('pos.clinic_team.col_name')}</TableCell>
                    <TableCell>{translate('pos.clinic_team.col_email')}</TableCell>
                    <TableCell>{translate('pos.clinic_team.col_title')}</TableCell>
                    <TableCell>{translate('pos.clinic_team.col_status')}</TableCell>
                    <TableCell>{translate('pos.clinic_team.col_alerts')}</TableCell>
                    <TableCell>{translate('pos.clinic_team.col_actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subs.map((sub: any) => (
                    <TableRow key={sub.id}>
                      <TableCell>{sub.name || '—'}</TableCell>
                      <TableCell>{sub.email}</TableCell>
                      <TableCell>{sub.professionalTitle || '—'}</TableCell>
                      <TableCell>
                        <ToneChip
                          label={translate(
                            `pos.clinic_team.status_${effectiveSubAccountStatus(sub)}`,
                          )}
                          tone={statusTone(effectiveSubAccountStatus(sub))}
                        />
                      </TableCell>
                      <TableCell>
                        {sub.emailAlertsEnabled === false
                          ? translate('pos.clinic_team.alerts_off')
                          : translate('pos.clinic_team.alerts_on')}
                      </TableCell>
                      <TableCell>
                        <SubAccountRowActions sub={sub} onChanged={() => refetch()} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {error && subs.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
              {translate('pos.clinic_team.list_unavailable')}
            </Typography>
          )}
        </CardContent>
      </Card>

      <AddSubAccountModal open={modalOpen} onClose={handleModalClose} />
    </Box>
  )
}

export default ClinicTeamList
